import {BANK_ACCOUNT_BALANCE_GLA_ID, DEFAULT_GLA_ID} from "../../../../../hooks/useGLAccounts.ts";
import type {BookingSource} from "../../types/types.ts";
import {ACCOUNTS_PAYABLE_GLA_CODE, ACCOUNTS_RECEIVABLE_GLA_CODE} from "../constants.ts";

export const GroupingAxis = {
    Bill: "bill",
    Transaction: "transaction",
    ManualBooking: "manualBooking",
    FixedAsset: "fixedAsset",
} as const;

export type GroupingAxis = (typeof GroupingAxis)[keyof typeof GroupingAxis];

export const GROUPING_AXES: readonly GroupingAxis[] = Object.values(GroupingAxis);

export type AxisIds = Readonly<Record<GroupingAxis, readonly string[]>>;

export const NO_AXIS_IDS: AxisIds = {
    [GroupingAxis.Bill]: [],
    [GroupingAxis.Transaction]: [],
    [GroupingAxis.ManualBooking]: [],
    [GroupingAxis.FixedAsset]: [],
};

function sourceAxisIds(source: BookingSource, axis: GroupingAxis): readonly string[] {
    switch (source.source_type_for_presentation) {
        case "BILL":
            return axis === GroupingAxis.Bill ? [source.bill_id] : [];
        case "TRANSACTION":
            return axis === GroupingAxis.Transaction ? [source.transaction_id] : [];
        case "MANUAL_BOOKING":
            return axis === GroupingAxis.ManualBooking ? [source.booking_id] : [];
        case "FIXED_ASSET":
            return axis === GroupingAxis.FixedAsset ? [source.fixed_asset_id] : [];
        case "MATCH":
            if (axis === GroupingAxis.Bill) return source.bill_ids ?? [];
            if (axis === GroupingAxis.Transaction) return source.transaction_ids ?? [];
            return [];
        default: {
            const _exhaustive: never = source;
            return _exhaustive;
        }
    }
}

export function axisIdsOf(sources: BookingSource[]): AxisIds {
    const collected: Record<GroupingAxis, Set<string>> = {
        [GroupingAxis.Bill]: new Set<string>(),
        [GroupingAxis.Transaction]: new Set<string>(),
        [GroupingAxis.ManualBooking]: new Set<string>(),
        [GroupingAxis.FixedAsset]: new Set<string>(),
    };
    for (const source of sources) {
        for (const axis of GROUPING_AXES) {
            for (const id of sourceAxisIds(source, axis)) {
                collected[axis].add(id);
            }
        }
    }
    const ids: Record<GroupingAxis, readonly string[]> = {...NO_AXIS_IDS};
    for (const axis of GROUPING_AXES) {
        ids[axis] = [...collected[axis]].sort();
    }
    return ids;
}

const AXIS_BY_ACCOUNT: Partial<Record<string, GroupingAxis>> = {
    [ACCOUNTS_PAYABLE_GLA_CODE]: GroupingAxis.Bill,
    [ACCOUNTS_RECEIVABLE_GLA_CODE]: GroupingAxis.Bill,
    [DEFAULT_GLA_ID]: GroupingAxis.Transaction,
    [BANK_ACCOUNT_BALANCE_GLA_ID]: GroupingAxis.Transaction,
};

export const FALLBACK_GROUPING_AXIS: GroupingAxis = GroupingAxis.Bill;

export function groupingAxisFor(accountCode: string): GroupingAxis {
    return AXIS_BY_ACCOUNT[accountCode] ?? FALLBACK_GROUPING_AXIS;
}

export function axisLabel(axis: GroupingAxis): string {
    switch (axis) {
        case GroupingAxis.Bill:
            return "per factuur";
        case GroupingAxis.Transaction:
            return "per transactie";
        case GroupingAxis.ManualBooking:
            return "per memoriaalboeking";
        case GroupingAxis.FixedAsset:
            return "per vast activum";
        default: {
            const _exhaustive: never = axis;
            return _exhaustive;
        }
    }
}
